<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <h4>Sent Follow-up Emails</h4>
        <p>The automatic follow-up emails that went to customers (and any no-reply emails), newest first.
          <span v-if="data && !data.allUsers">You see the quotes you follow up.</span></p>
        <div class="p-fluid p-formgrid p-grid">
          <div class="p-field p-col-12 p-md-2">
            <label for="sentFrom">From</label>
            <Calendar id="sentFrom" v-model="form.from" dateFormat="dd/mm/yy" :showIcon="true" />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="sentTo">To</label>
            <Calendar id="sentTo" v-model="form.to" dateFormat="dd/mm/yy" :showIcon="true" />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="sentCustomer">Customer</label>
            <InputText id="sentCustomer" v-model="form.customer" @keyup.enter="search" />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="sentContact">Contact</label>
            <InputText id="sentContact" v-model="form.contact" @keyup.enter="search" />
          </div>
          <div class="p-field p-col-12 p-md-2">
            <label for="sentQuoteNo">Quote number</label>
            <InputText id="sentQuoteNo" v-model="form.quoteNo" @keyup.enter="search" />
          </div>
          <div v-if="!data || data.allUsers" class="p-field p-col-12 p-md-2">
            <label for="sentUser">Salesperson</label>
            <Dropdown id="sentUser" v-model="form.userId" :options="userOptions" optionLabel="name" optionValue="id"
              :filter="true" placeholder="Everyone" />
          </div>
        </div>
        <div class="p-d-flex p-mb-3">
          <Button id="sentSearch" label="Search" icon="pi pi-search" style="width: auto" class="p-mr-2" @click="search" />
          <Button label="Clear" icon="pi pi-times" class="p-button-text" style="width: auto" @click="clear" />
        </div>
        <DataTable :value="rows" :loading="loading" dataKey="logId" class="p-datatable-sm" :rowHover="true"
          :lazy="true" :paginator="true" :rows="size" :totalRecords="total" @page="onPage"
          emptyMessage="No follow-up emails were sent for this search.">
          <Column field="sentAt" header="Sent" />
          <Column header="Quote">
            <template #body="slot">
              <router-link :to="'/enquiries/' + slot.data.oppId + '/followup'">{{ slot.data.quoteRef }}</router-link>
            </template>
          </Column>
          <Column field="customerName" header="Customer" />
          <Column field="contactName" header="Contact" />
          <Column header="Email">
            <template #body="slot">{{ slot.data.what }}: {{ slot.data.subject }}</template>
          </Column>
          <Column header="To">
            <template #body="slot">{{ to(slot.data) }}</template>
          </Column>
          <Column field="salespersonName" header="Salesperson" />
          <Column header="" :styles="{ width: '6rem' }">
            <template #body="slot">
              <Button :id="'viewSent' + slot.data.logId" label="View" icon="pi pi-eye" class="p-button-text p-button-sm"
                @click="view(slot.data)" />
            </template>
          </Column>
        </DataTable>
      </div>
    </div>
    <Dialog :visible.sync="viewing" header="Follow-up email as sent" :modal="true" :style="{ width: '720px' }"
      :contentStyle="{ maxHeight: '70vh', overflowY: 'auto' }">
      <div v-if="email" id="sentEmailView">
        <p class="p-mt-0"><b>Sent:</b> {{ email.sentAt }}<br /><b>To:</b> {{ to(email) }}<br /><b>Quote:</b> {{ email.quoteRef }}
          ({{ email.what }})<br /><b>Subject:</b> {{ email.subject }}</p>
        <pre class="sent-body">{{ email.body }}</pre>
      </div>
    </Dialog>
  </div>
</template>

<script>
import FollowupService, { errorMessage } from '@/service/FollowupService'
import UserService from '@/service/UserService'
import { sentFilterBody, sentTo } from './sentEmails'

const empty = () => ({ from: null, to: null, customer: '', contact: '', quoteNo: '', userId: null })

export default {
  // item (e), batch 3: each user sees the quotes they follow up; Admin sees all and can pick a salesperson
  name: 'SentFollowupEmails',
  data: () => ({ followupService: new FollowupService(), form: empty(), data: null, page: 0, size: 50, loading: false,
    users: [], viewing: false, email: null }),
  computed: {
    rows() { return (this.data && this.data.rows) || [] },
    total() { return (this.data && this.data.total) || 0 },
    userOptions() {
      return [{ id: null, name: 'Everyone' }].concat(this.users.map(u => ({ id: u.id, name: [u.firstName, u.lastName].filter(Boolean).join(' ') })))
    }
  },
  created() {
    new UserService().getAllActiveUsers().then(d => { this.users = d || [] }).catch(() => {})
    this.load()
  },
  methods: {
    to: sentTo,
    load() {
      this.loading = true
      return this.followupService.sentEmails(sentFilterBody(this.form, this.page, this.size))
        .then(d => { this.data = d })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: errorMessage(err, 'The sent emails could not be loaded.'), life: 5000 })
        })
        .finally(() => { this.loading = false })
    },
    search() {
      this.page = 0
      return this.load()
    },
    clear() {
      this.form = empty()
      return this.search()
    },
    onPage(e) {
      this.page = e.page
      this.size = e.rows
      return this.load()
    },
    view(row) {
      return this.followupService.sentEmail(row.logId)
        .then(d => { this.email = d; this.viewing = true })
        .catch(err => {
          this.$toast.add({ severity: 'error', summary: 'Not loaded', detail: errorMessage(err, 'The email could not be loaded.'), life: 5000 })
        })
    }
  }
}
</script>

<style scoped>
.sent-body { white-space: pre-wrap; font-family: inherit; background: #f8f9fa; padding: 0.75rem; border-radius: 4px; }
</style>
